/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/

/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import rootReducer from 'redux/reducers/root';
// higher order reducer for redux that enables batching subscriber notifications for an array of actions.
import { enableBatching } from 'redux-batched-actions';
import { convertStylesPXtoEM } from 'utils/styles';
// utils
import { log } from 'utils/logger';
import { isLocalStorageExists, hostname } from 'utils/environment';


/* ========== ~~~~~~~~~~ STORE : APPLICATION STATE MANAGER ~~~~~~~~~~ ========== */
// STORE
// configure our store of application state
// index.js file will utilize this via "provider" functionality from redux to be passed down and accessible via child components
const configureStore = (settings = {}) => {

    // preloaded state object that will get populated via processing settings and data
    const initialState = {
        settings: {},
        contentStatus: {},
        queueStatus: {},
        contentData: {},
        embedStatus: {},
        sessionStatus: {},
        screenStatus: { name: null, type: null, visible: null, component: null, stored: null },
        rewards: { settings: {}, status: {} }
    };


    // +++++++ EMBED SETTINGS +++++++ //
    initialState.settings = {
        embedSettings: {
            uuid: settings.embed.uuid,
            site: settings.site,
            inView: settings.embed.inView,
            googleAnalyticsID: settings.embed.googleAnalyticsID,
            elements: settings.embed.elements,
            styles: settings.embed.styles,
            ads: settings.embed.ads,
            embedUUID: settings.embed.uuid,
            useTestingLogic: settings.embed.useTestingLogic,
            version: settings.embed.version,
            isPubPortalPreview: typeof settings.embed.isPubPortalPreview !== 'undefined' ? settings.embed.isPubPortalPreview : false,
            enableInsticatorBranding: typeof settings.embed.enableInsticatorBranding !== 'undefined' ? settings.embed.enableInsticatorBranding : true,
            enableABTest: settings.embed.enableABTest ? settings.embed.enableABTest : false,
            partnerLogo: null,
            enlargedFont: typeof settings.embed.enlargedFont !== 'undefined' && isLargeFontEligibleHost() ? settings.embed.enlargedFont : false
        },
        contentSettings: {}
    };


    // ~~~~~~~ EMBED STATUS ~~~~~~~ //
    initialState.embedStatus = {
        contents: [],
        inView: null, // null, true, false
        inViewRecorded: null, // null, true, false
        integrationType: null,
        url: null,
        isABTest: settings.embed.enableABTest ? settings.enableABTest : false,
        abTestVersion: settings.embed.enableABTest ? _getABTestBucket() : null,
        outOfContent: true,
        showLoader: false
    };


    // shortcut to read this from the window in tracking (need to adjust this is TERRIBLE)
    window.abTestVersion = initialState.embedStatus.abTestVersion;


    // ~~~~~~~ REWARDS ~~~~~~~ //
    initialState.rewards = {
        settings: settings.embed.rewards,
        status: {
            rewardsPermitted: false,
            pointsEarned: 0,
            numberOfRewardsEarned: 0,
            rewardEarnedPhase: false,
            showExpandedMessage: false,
            marketResearch: {}
        }
    };


    // ~~~~~~~ SESSION STATUS ~~~~~~~ //
    initialState.sessionStatus = {
        userInfo: {
            userExists: null,
            userId: null,
            dmpCookieId: null,
            showPrivacyPolicy: null
        },
        userData: {},
        language: null,
        languages: null,
        cookieEnabled: null,
        userAgent: null,
        os: null,
        pageurl: null,
        device: null,
        ip: null,
        userGeo: null,
        GDPRConsentRequired: null,
        excludedQuestionnaires: [],
        dataFetchingPermitted: false,
        dmpCallsEnabled: false,
        cookieCheckDone: false
    };

    // ~~~~~~~ QUEUE STATUS ~~~~~~~ //
    initialState.queueStatus = {
        queue: {
            queue: [],
            contentTypes: {}
        },
        currentContent: null,
        currentContentType: null,
        nextContent: null,
        nextContentType: null,
        queueCompleted: false,
        repeatQueue: true,
        contentOrder: [],
        frameOrder: [],
    };


    // loop content ___________________
    settings.embed.content.forEach(content => {

        initialState.embedStatus.contents.push(content.id);

        // +++++++ CONTENT SETTINGS +++++++ //
        initialState.settings.contentSettings[content.id] = content;
        initialState.settings.contentSettings[content.id]['injectedWithSettings'] = {};


        // ------- CONTENT DATA ------- //
        initialState.contentData[content.id] = {
            isCollection: content.isCollection,
            shouldFetch: false,
            ready: false,
            data: [],
            storage: {}
        };

        if(content.type === 'questionnaire') {
            initialState.contentData[content.id]['questionnaires'] = null
        }

        if(content.type === 'marketResearch') {
            initialState.rewards.status['marketResearch'][content.id] = { marketResearchProvider: null, marketResearchPoints: null }
        }

        if(content.type === 'mixed') {
            initialState.embedStatus['numberOfQuestionsLimit'] = content.uniqueProps.numberOfQuestionsLimit;
        }

        // ~~~~~~~ CONTENT STATUS ~~~~~~~ //
        initialState.contentStatus[content.id] = {
            type: content.type,
            status: 'inactive',
            frame: 0,
            totalFrameCount: 0, // should come from unique content since that's where some data will be processed
            showFrameForXSeconds: content.showFrameForXSeconds,
            clearTimeout: false,
            paused: false,
            injectedWithPauseType: null,
            injectedWithDisplayType: null,
            hearted: {},
            uniqueState: {},
            // injectAtFrame: null,
            // repeatEveryXFrames: null,
            parentContentId: null,
            injectedContentIds: [],
            injectedLayoutIds: [],
            injectablesAtFrame: {},
        };

        if(content.type === 'questionnaire') {
            initialState.contentStatus[content.id]['uniqueState'] = { currentQuestionnaireIdx: null }
        }

        if(content.type === 'userDataCollection') {
            initialState.contentStatus[content.id]['uniqueState'] = {
                udcId: null,
                contentSignature: null,
                contentProperties: null,
                phase: null
            }
        }

        // .......... SETUP THE QUEUE  (step 1) .......... //
        // store content ids based on queuePosition node (this can also be formed based on position in the array instead of queuePosition property ... need to confirm with BE)
        if (content.queuePosition !== null) {
            initialState.queueStatus.queue.queue[content.queuePosition] = content.id;
            initialState.queueStatus.queue.contentTypes[content.id] = content.type;
            initialState.queueStatus.contentOrder[content.queuePosition] = { id: content.id, type: content.type, frames: [] };
        }

        // currently we don't have partnerLogo on the embed level in settings
        // instead we have it on an individual content level only
        // this will set a variable is embedSettings assuming every individual content level partnerLogo setting is the same
        // needs to be redone with the BE support
        if(!!content.elements && !!content.elements.header && content.elements.header.partnerLogo !== undefined) {
            initialState.settings.embedSettings.partnerLogo = content.elements.header.partnerLogo.visible;
        }
    });
    // ___________________end content loop



    // .......... SET THE QUEUE (step 2) .......... //
    initialState.queueStatus.queue.queue = Object.values(initialState.queueStatus.queue.queue);
    // combines content and frames into one full queue (both content/frames mixed together in the order in which they will show to the user)
    let combinedOrderIndex = -1;
    initialState.queueStatus.contentOrder.forEach((orderedContent, cIndex) => {
        if (orderedContent.frames.length > 0) {
            orderedContent.frames.forEach((orderedFrames, fIndex) => {
                combinedOrderIndex += 1;
                initialState.queueStatus['frameOrder'][combinedOrderIndex] = { id: orderedFrames.id, type: orderedFrames.type };
            });
        } else {
            combinedOrderIndex += 1;
            initialState.queueStatus['frameOrder'][combinedOrderIndex] = { id: orderedContent.id, type: orderedContent.type };
        }
    });


    Object.entries(initialState.settings.contentSettings).forEach(([currentContentId, currentContentSettings]) => {
        currentContentSettings.injectables.forEach(injectable => {
            if (injectable.type === 'content') {
                initialState.settings.contentSettings[injectable.id]['injectedWithSettings'] = injectable;
            }
        });
    });
    // return the created store :)
    return createStore(
        enableBatching(rootReducer),
        initialState,
        applyMiddleware(thunk),
    )
};
const _getABTestBucket = () =>  null;
// const _getABTestBucket = () => 'COUNTDOWN';
const isLargeFontEligibleHost =() => ['www.decaturdaily.com', 'decaturdaily.com', 'testthisshit.online', 'ppa.hunchme.com', 'ppa.insticator.com', 'localhost'].includes(hostname);

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default configureStore;
